import { type SignInFormSchema } from "@/components/blocks/AuthModal.data";
import { initializeApp } from "firebase/app";
import {
  type AuthError,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { ENV } from "./env";

const app = initializeApp({
  apiKey: ENV.FIREBASE_API_KEY,
  authDomain: ENV.FIREBASE_AUTH_DOMAIN,
  projectId: ENV.FIREBASE_PROJECT_ID,
  storageBucket: ENV.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.FIREBASE_APP_ID,
  measurementId: ENV.FIREBASE_MEASUREMENT_ID,
});

// Initialize Firebase Authentication and get a reference to the service
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
// TODO: handle firebase token refresh

export async function signInWithCredentials({
  email,
  password,
}: SignInFormSchema) {
  try {
    const firebaseUser = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return firebaseUser.user.getIdToken();
  } catch (e) {
    throw new Error(formatAuthError((e as AuthError).code).message);
  }
}

export async function signInWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user.getIdToken();
  } catch (e) {
    throw new Error(formatAuthError((e as AuthError).code).message);
  }
}

export async function signOut() {
  await firebaseSignOut(auth);
}

export async function signup({ email, password }: SignInFormSchema) {
  const firebaseUser = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );
  return firebaseUser.user.getIdToken();
}

const formatAuthError = (errorCode: string) => {
  switch (errorCode) {
    case "auth/email-already-in-use":
      return {
        cause: "error",
        message: "An account with this email already exists.",
      };
    case "auth/weak-password":
      return {
        cause: "error",
        message: "Password is too weak. Please choose a stronger password.",
      };
    case "auth/user-not-found":
      return {
        cause: "error",
        message: "No account found with this email address.",
      };
    case "auth/wrong-password":
      return {
        cause: "error",
        message: "Incorrect password. Please try again.",
      };
    case "auth/invalid-email":
      return { cause: "error", message: "Please enter a valid email address." };
    case "auth/invalid-credential":
      return { cause: "error", message: "Invalid credentials." };
    case "auth/user-disabled":
      return { cause: "error", message: "This account has been disabled." };
    case "auth/too-many-requests":
      return {
        cause: "error",
        message: "Too many failed attempts. Please try again later.",
      };
    case "auth/popup-closed-by-user":
      return {
        cause: "user",
        message: "Google sign-in was cancelled.",
      };
    default:
      return {
        cause: "error",
        message: "An error occurred. Please try again.",
      };
  }
};
